import { ChangeDetectorRef, Component, OnInit, ElementRef, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { fromSelectors } from '@store/selectors';
import { fromActions } from '@store/actions';
import { Store } from '@ngrx/store';
import { BroadcastService } from '@services/broadcast-service';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { WindowRefService } from '@services/window-ref-service';
import { CartParams } from '@store/modals';
import { Location } from '@angular/common';
import { LocalStorageService } from '@services/local-storage-service';
import { AppConfig } from '../app.config';
import { ExperimentService } from '@services/experiment/experiment.service';
import { UserService } from '@services/user/user.service';
import { resetCacheByName } from '@services/connection-service/cache-interceptor';
import { PersonalisedPromotionsService } from '@services/personalised-promotions-service';
import { AovService } from '@services/aov-service/aov.service';
import { OrderFlowStep } from '@components/order-flow-steps/order-flow-steps.types';

@Component({
  selector: 'product-cart',
  templateUrl: './cart.html',
  standalone: false,
})
export class ProductCartComponent implements OnInit {
  @ViewChild('billDetailsContainer', { static: false }) billDetailsContainer: ElementRef<HTMLElement>;
  user: any;
  cart: any;
  cartItems: any = [];
  totalProducts: number;
  amountRemaining: number;
  aovExp: boolean = false;
  isAOVActive: boolean = false;
  paramsAdd: boolean = false;
  noProductsInCart: boolean = false;
  isLoading: boolean = true;
  maxPrice: any;
  experiments: any[];
  minimumCash: any;
  cashBalance: any;
  tempCashBalance: any;
  showCashBalance: boolean = false;
  lastVisitedURL: any;
  freeProduct: any;
  rxLimit: number = 3;
  nonRxLimit: number = 5;
  noFreeProduct: boolean = false;
  showFreeProd: boolean;
  removeFreeGift: boolean;
  productsInCart: { [key: string]: number } = {};
  productsQuantity: { [key: string]: any } = {};
  productsInParams: { [key: string]: number } = {};
  routesList: any = [];
  discontinuedList: any = [];
  newProduct: any;
  oldProduct: any;
  popUp: any = {};
  isDiscontinued: boolean = false;
  totalMRP: number = 0;
  totalSP: number = 0;
  aovCartValue: number = 0;
  discount: number = 0;
  startTimer: number;
  newCartUI: boolean = false;
  showSavedText: boolean = false;
  showViewDetailedButtonContainer: boolean = true;
  freeProductsArray: any[] = [];
  freeProductImageForAov: string;
  aovBannerPercentageDone: number = 0;
  newCartStickyBillDetails: boolean = false;
  orderFlowHeader: boolean = false;
  readonly OrderFlowStep = OrderFlowStep;

  constructor(
    private broadcast: BroadcastService,
    private conn: ConnectionService,
    private router: Router,
    private eventLogger: EventLoggerService,
    protected appConfig: AppConfig,
    private windowRef: WindowRefService,
    private localStorage: LocalStorageService,
    private route: ActivatedRoute,
    private store: Store,
    private location: Location,
    private changeDetectionRef: ChangeDetectorRef,
    public readonly experimentService: ExperimentService,
    private userService: UserService,
    private personalisedPromotionsService: PersonalisedPromotionsService,
    private aovService: AovService,
  ) {
  }

  async ngOnInit(): Promise<void> {
    this.startTimer = new Date().getTime();
    this.store.select(fromSelectors.selectCartProducts).subscribe((products: any): any => this.productsInCart = { ...products });
    this.user = this.conn.getActingUser();
    if (this.userService.isUnpaidUser(this.user)) {
      resetCacheByName('getCart');
    }
    this.experiments = await this.conn.findUserActiveExperiments();
    await this.getExperiments();
    await this.experimentService.experimentsLoaded;
    this.orderFlowHeader = this.experimentService.isOrderFlowHeaderEnabled();
    this.minimumCash = this.appConfig.Shared.CART_PAGE_CASH_VISIBILITY_MIN_CASH;
    await this.getCashBalance();
    this.tempCashBalance = this.cashBalance;
    if ((this.cashBalance >= this.minimumCash) && !this.experimentService.applyCashExperiment) {
      this.showCashBalance = true;
    }
    await this.getCart();
    await this.modifyCartByParams();
    this.routesList = this.windowRef.nativeWindow.historyStates;
    this.noFreeProduct = this.localStorage.getValue('freeProduct');
    if (this.noFreeProduct) this.removeFreeGift = true;
    await this.getCart();
    if (this.routesList.length > 2) {
      this.lastVisitedURL = this.routesList[this.routesList.length - 2];
      if (this.lastVisitedURL.url.includes('user/order/reorder')) {
        const routesListTemp = [];
        routesListTemp[0] = { url: 'user?tab=home&init=true' };
        routesListTemp[1] = { url: this.lastVisitedURL.url };
        routesListTemp[2] = { url: 'cart' };
        this.windowRef.nativeWindow.historyStates = routesListTemp;
      }
    }
    this.getProductQuantity();
    this.fetchDiscount();
    this.isLoading = false;

    const time = new Date().getTime() - this.startTimer;
    this.eventLogger.trackEvent('cart_page_loading_time', { timeInMilliSec: time, userId: this.user.id });
    this.changeDetectionRef.detectChanges();
    this.setupIntersectionObserver();
    this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ pageName: 'cart', no_of_products: this.totalProducts }), [
      this.appConfig.Shared.EventTracker.MIXPANEL,
      this.appConfig.Shared.EventTracker.AMPLITUDE,
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
  }

  /** The AOV threshold is met, so the free product is part of this cart. */
  private get isAovFreeProductInCart(): boolean {
    return !!this.freeProduct && this.aovCartValue >= this.maxPrice?.totalValue;
  }

  /** order_flow_header shows the free product at the top of the cart list. */
  get shouldShowAovFreeProductFirst(): boolean {
    return this.isAovFreeProductInCart && this.orderFlowHeader;
  }

  /** Legacy cart keeps the free product last, below every paid item. */
  get shouldShowAovFreeProductLast(): boolean {
    return this.isAovFreeProductInCart && !this.orderFlowHeader;
  }

  get freeProductQuantityLabel(): string {
    return [this.freeProduct?.quantity, this.freeProduct?.quantityUnit].filter(Boolean).join(' ');
  }

  async getExperiments(): Promise<void> {
    this.isAOVActive = await this.personalisedPromotionsService.isAOVActiveInPersonalisedPromotions(this.user);
    // sequential (not forEach) so callers awaiting getExperiments see the AOV product resolved.
    for (const experiment of this.experiments) {
      if (experiment.key === 'aov_gamification' && this.isAOVActive) {
        this.aovExp = true;
        this.maxPrice = experiment.variant;
        const freeProductDetails = await this.aovService.getFreeProduct(this.maxPrice);
        this.freeProduct = freeProductDetails.catalog;
        this.freeProductImageForAov = freeProductDetails.image;
      } if (experiment.key === 'new_cart_redesign') {
        this.newCartUI = true;
        this.showSavedText = !!experiment?.variant?.showSavedText;
      } if (experiment.key === 'new_cart_sticky_bill_details') {
        this.newCartStickyBillDetails = true;
      }
    }
  }

  setupIntersectionObserver(): void {
    const options = {
      root: null,
      rootMargin: '0px 0px -112px 0px',
      threshold: [0, 0.1, 0.2, 0.3, 1],
    };

    const observer = new IntersectionObserver(([entry]: IntersectionObserverEntry[]): void => {
      const isVisible = entry.intersectionRatio >= 0.001;
      this.onIntersection(isVisible);
    }, options);
    if (this.billDetailsContainer?.nativeElement) {
      observer.observe(this.billDetailsContainer.nativeElement);
    }
  }

  async getCashBalance(): Promise<void> {
    if (!this.userService.isPaidUser(this.user)) {
      return;
    }
    this.cashBalance = await this.conn.fetchCashBalance();
  }

  onIntersection(isIntersecting: boolean): void {
    if (this.showViewDetailedButtonContainer !== isIntersecting) {
      this.showViewDetailedButtonContainer = isIntersecting;
    }
  }

  async modifyCartByParams(): Promise<void> {
    const type: string = this.route.snapshot.queryParams?.type || '';
    const productIds: string[] = this.route.snapshot.queryParams?.products?.split(',');
    if (type === 'PRODUCT' && productIds?.length) {
      this.paramsAdd = true;
      const products = JSON.parse(JSON.stringify(await this.conn.findProductsById(productIds)));
      await this.addToCart(productIds, products);
    }

    if (type === 'REGIMEN' && productIds?.length) {
      const filteredProductIds = productIds
        .filter((id: string): boolean => !this.cartItems.find((each: any): any => each.productId === id));
      const products = JSON.parse(JSON.stringify(await this.conn.findProductsById(filteredProductIds)));
      await this.addToCart(filteredProductIds, products);
    }
  }

  async addToCart(productIds: string[], products: any[]): Promise<void> {
    // eslint-disable-next-line no-restricted-syntax
    for (const productId of productIds) {
      const quantityInCart: any = this.cartItems.find((each: any): any => each.productId === productId)?.quantity || 0;
      const limit: any = products.find((each: any): any => each.objectId === productId)?.prescriptionRequired
        ? this.rxLimit : this.nonRxLimit;

      const request = Math.min(limit - quantityInCart, 1)
        ? this.conn.addProductToCart({ productId, quantity: Math.min(limit - quantityInCart, 1) })
        : Promise.resolve();
      // eslint-disable-next-line no-await-in-loop
      await request;
    }

    await this.checkForFreeProduct('add');
  }

  /**
   *
   * @param isAddOrRemove is used to fetch the quantity of product on add or remove of a item
   */

  async getCart(isAddOrRemove: boolean = false): Promise<void> {
    await this.fetchCart();
    this.fetchDiscount();
    this.totalProducts = 0;
    if (this.cartItems.length) {
      this.cartItems.forEach((each: any): void => {
        this.totalProducts += each.quantity;
        this.productsInCart[each.productId] = each.quantity;
        if (isAddOrRemove) {
          // eslint-disable-next-line no-param-reassign
          each.quantityData = this.productsQuantity[each.productId];
        }
      });
    }
    if (!this.cartItems.length) {
      this.noProductsInCart = true;
    } else {
      this.noProductsInCart = false;
    }
    if (this.cartItems.length && this.aovExp) {
      this.setAOVVariables();
      await this.getProductQuantity();
      if (this.amountRemaining > 0) {
        const product = this.cartItems.find((each: any): any => each.price === 0);
        if (product) {
          await this.removeProduct(product, 'DELETE');
          this.showFreeProd = false;
        }
      } else {
        this.amountRemaining = 0;
      }
      const prod = this.cartItems.indexOf(this.cartItems.find((each: any): any => each.price === 0));
      if (prod >= 0) {
        const len = this.cartItems.length;
        const ret = this.cartItems[prod];
        for (let i = prod + 1; i < len; i += 1) {
          this.cartItems[i - 1] = this.cartItems[i];
        }
        this.cartItems.length = len - 1;
        this.cartItems.push(ret);
      }
    }
    if (this.aovExp) {
      await this.checkForFreeProduct('check');
    } else {
      const product = this.cartItems.find((each: any): any => each.price === 0);
      this.removeProduct(product, 'DELETE');
    }
    this.removeProductsWithPriceZero();
  }

  setAOVVariables(): void {
    this.aovCartValue = this.calculateCartValueBeforeCash();
    this.freeProductsArray = this.cartItems.filter((each: any): any => each.price === 0);
    this.amountRemaining = this.maxPrice.totalValue - this.aovCartValue;
    this.aovBannerPercentageDone = Math.round((this.aovCartValue / this.maxPrice.totalValue) * 100);
  }

  private calculateCartValueBeforeCash(): number {
    const total = (this.cartItems || []).reduce((runningTotal: number, element: any): number => {
      const quantity = this.productsInCart?.[element.productId];
      return quantity ? runningTotal + (quantity * element.price) : runningTotal;
    }, 0);
    return Math.floor(total);
  }

  async fetchCart(): Promise<void> {
    const cartData = await this.conn.fetchNewCart(this.user.id);
    this.cart = cartData?.data;
    this.cartItems = this?.cart?.lineItems;
  }

  async removeProductsWithPriceZero(): Promise<any> {
    const product = this.cartItems.find((each: any): any => each.price === 0);
    if (product?.quanity > 1) {
      const params = {
        productId: this.maxPrice.productID,
        quantity: 1,
      };
      await this.conn.removeProductFromCart(params);
      this.getCart();
    }
  }

  async getProductQuantity(): Promise<any> {
    let productsFromParams;
    this.cartItems.forEach(async (element: any): Promise<any> => {
      [productsFromParams] = await this.conn.findProductsById(element.productId);
      if (productsFromParams) {
        // eslint-disable-next-line no-param-reassign
        element.quantityData = `${productsFromParams?.get('quantity')} ${productsFromParams?.get('quantityUnit')}`;
        // eslint-disable-next-line no-param-reassign
        element.isRx = productsFromParams.get('prescriptionRequired');
        // eslint-disable-next-line no-param-reassign
        element.inventoryStatus = productsFromParams.get('inventoryStatus');
        const bgImage = productsFromParams?.get('rebrandedImageWithBackground');
        // eslint-disable-next-line no-param-reassign
        element.rebrandedImage = bgImage ? bgImage[0] : '';
      }
      this.productsQuantity[element.productId] = element.quantityData;
      if (this.discontinuedList?.length) {
        const prod: any = this.discontinuedList?.find((each: any): any => each.productId === element.productId);
        if (!prod && element.inventoryStatus
      && element.inventoryStatus === this.appConfig.Shared.Inventory.Type.DISCONTINUED) {
          this.discontinuedList.push(element);
          this.checkdiscontinuedProduct(true);
        }
      }
    });
  }

  fetchDiscount(): any {
    this.totalMRP = 0;
    this.totalSP = 0;
    this.cartItems.forEach((element: any):any => {
      if (this.productsInCart[element.productId]) {
        this.totalSP += this.productsInCart[element.productId] * element.price;
        this.totalMRP += this.productsInCart[element.productId] * element.mrp;
      }
    });
    this.totalMRP = Math.floor(this.totalMRP);
    this.totalSP = Math.floor(this.totalSP);
    this.aovCartValue = this.calculateCartValueBeforeCash();
    this.cashBalance = this.tempCashBalance;
    if (this.showCashBalance) {
      const cashDiscount = Math.min(this.cashBalance, this.totalSP);
      this.totalSP -= cashDiscount;
      this.cashBalance = cashDiscount;
    }
    let discount = 0;
    this.totalMRP = Math.floor(this.totalMRP);
    if (this.totalMRP > this.totalSP) {
      discount = Math.floor(((this.totalMRP - this.totalSP) * 100) / this.totalMRP);
    }
    this.discount = discount;
  }
  async checkForFreeProduct(value?: any): Promise<void> {
    const product = this.cartItems.find((each: any): any => each.price === 0);
    const freeProduct = this.localStorage.getValue('freeProduct');
    if (product) this.showFreeProd = true;

    // Ensure we have the latest totalSP value
    await this.fetchDiscount();

    // Skip adding free product if we're removing products
    if (value === 'check' && !product && !freeProduct && value !== 'remove') {
      // Add free product logic
      if (this.aovCartValue >= this.maxPrice.totalValue) {
        const found = this.cartItems.some((el: any): any => el.productId === this.maxPrice.productID);
        if (!found) {
          const params1 = {
            productId: this.maxPrice.productID,
            quantity: 1,
          };
          await this.conn.addProductToCart(params1);
          this.productsInCart[this.maxPrice.productID] = 1;
          await this.getCart();
        }
      }
    }

    // Remove free product logic
    if (value === 'check' && product && this.aovCartValue < this.maxPrice.totalValue) {
      const params1 = {
        productId: this.maxPrice.productID,
        quantity: 1,
      };
      await this.removeProduct(params1, 'DELETE');
      this.showFreeProd = false;
      await this.getCart();
      await this.fetchDiscount();
      this.setAOVVariables();
    }
  }

  // eslint-disable-next-line complexity
  async addOrRemove(product: any, action: string, event?: any): Promise<void> {
    if (event) event.stopPropagation();
    const quantity = this.productsInCart[product.productId] || 0;
    const [productsFromParams]: any = await this.conn.findProductsById(product.productId);
    if (productsFromParams) {
      // eslint-disable-next-line no-param-reassign
      product.prescriptionRequired = productsFromParams.get('prescriptionRequired');
      // eslint-disable-next-line no-param-reassign
      product.inventoryStatus = productsFromParams.get('inventoryStatus');
      if (action === 'ADD' && !product.prescriptionRequired && quantity === this.nonRxLimit) {
        this.broadcast.broadcast('NOTIFY', { message: 'Maximum quantity is limited to 5 per product' });
        return;
      }
      if (action === 'ADD' && product.prescriptionRequired && quantity === this.rxLimit) {
        this.broadcast.broadcast('NOTIFY', { message: 'Maximum quantity is limited to 3 per product' });
        return;
      }
      if (action === 'ADD' && !product.prescriptionRequired && this.productsInCart[product.productId] < this.nonRxLimit) {
        await this.callAddProductsAPIWhenLessThanNonRxLimit(product, quantity);
      }
      if (action === 'ADD' && product.prescriptionRequired && this.productsInCart[product.productId] < this.rxLimit
      ) await this.callAddProductsAPIWhenLessThanRxLimit(product, quantity);
    }
    this.store.dispatch(fromActions.CartUpdateProductsBegin({ products: this.productsInCart }));
    await this.checkForFreeProduct('add');
  }

  async callAddProductsAPIWhenLessThanNonRxLimit(product: any, quantity: number): Promise<void> {
    this.productsInCart[product.productId] = quantity + 1;
    const res = await this.getAddProductResponse(product);
    if (res) {
      const cartItems = res?.lineItems;
      cartItems.forEach((element: any): any => {
        if (element.productId === product.productId && element.quantity > this.nonRxLimit) {
          this.removeProduct(product);
        }
      });
      this.getCart(true);
      this.fetchDiscount();
    }
  }

  async callAddProductsAPIWhenLessThanRxLimit(product: any, quantity: number): Promise<void> {
    this.productsInCart[product.productId] = quantity + 1;
    const res: any = await this.conn.addProductToNewCart({
      productId: product.productId,
      quantity: 1,
      section: product.section,
    });
    if (res) {
      const cartItems = res?.lineItems;
      cartItems.forEach((element: any): any => {
        if (element.productId === product.productId && element.quantity > this.rxLimit) {
          this.removeProduct(product);
        }
      });
      this.getCart(true);
      this.fetchDiscount();
    }
  }

  async getAddProductResponse(product: any): Promise<any> {
    return this.conn.addProductToNewCart({
      productId: product.productId,
      quantity: 1,
      section: product.section,
    });
  }

  async removeProduct(product?: any, action?: string, event?: any, params?: CartParams): Promise<void> {
    if (event) event.stopPropagation();
    if (!product) return;
    const params1: CartParams = {
      quantity: 0,
      productId: undefined,
    };

    // Calculate new totalSP before removing product
    let newTotalSP = this.aovCartValue;
    if (product.price > 0) { // Only subtract if it's not the free product
      newTotalSP -= product.price * (action === 'REMOVE' ? 1 : product.quantity);
    }
    // If removing a regular product would put us below 800, remove free product first
    if ((newTotalSP < this?.maxPrice?.totalValue) && this.aovExp) {
      const freeProduct = this.cartItems.find((each: any): any => each.price === 0);
      if (freeProduct) {
        const freeProductParams = {
          productId: freeProduct.productId,
          quantity: freeProduct.quantity,
        };
        await this.callRemoveProductFromCartAPI(null, freeProductParams);
        this.showFreeProd = false;
      }
    }
    // Remove the requested product
    this.calculateQuantityToRemoveFromCart(product, params1, action);
    await this.callRemoveProductFromCartAPI(params, params1);

    this.store.dispatch(fromActions.CartUpdateProductsBegin({ products: this.productsInCart }));
    await this.getCart(true);
    await this.checkForFreeProduct('remove');
    await this.fetchDiscount();
    this.setAOVVariables();
  }

  calculateQuantityToRemoveFromCart(product: any, params1: CartParams, action: string): void {
    const quantity = this.productsInCart[product.productId] || 0;
    // eslint-disable-next-line no-param-reassign
    params1.productId = product.productId;
    if (action === 'REMOVE' && product?.quantity > 0) {
      this.productsInCart[product.id] = quantity - 1;
      // eslint-disable-next-line no-param-reassign
      params1.quantity = 1;
    }
    if (action === 'DELETE') {
      this.productsInCart[product.id] = quantity - 1;
      // eslint-disable-next-line no-param-reassign
      params1.quantity = product.quantity;
    }
  }

  async callRemoveProductFromCartAPI(params: CartParams, params1: CartParams): Promise<void> {
    const qParams = params || params1;
    await this.conn.removeProductFromNewCart(qParams);
  }

  viewDetails(): void {
    const queryParams = { showAll: true };
    this.router.navigate(['user/order/reorder'], { queryParams });
  }

  async removeFreeProd(type: string): Promise<void> {
    this.removeFreeGift = !this.removeFreeGift;
    let freeProduct: boolean;
    const params = {
      productId: this.maxPrice.productID,
      quantity: 1,
    };
    const found = this.cartItems.some((el: any): any => el.productId === this.maxPrice.productID);
    switch (type) {
      case 'add':
        if (!found) {
          await this.conn.addProductToCart(params);
          this.localStorage.delete('freeProduct');
          this.showFreeProd = true;
          this.noFreeProduct = false;
        }
        break;
      case 'remove':
        await this.conn.removeProductFromCart(params);
        freeProduct = true;
        this.localStorage.setValue('freeProduct', freeProduct);
        this.showFreeProd = false;
        break;
      default:
        break;
    }
    await this.fetchCart();
    this.getProductQuantity();
  }

  back(): void {
    this.broadcast.broadcast('NAVIGATION_BACK');
    this.eventLogger.cleverTapEvent('backClicked', JSON.stringify({ name: 'cart' }), [
      this.appConfig.Shared.EventTracker.MIXPANEL,
      this.appConfig.Shared.EventTracker.AMPLITUDE,
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
  }

  async checkdiscontinuedProduct(checkAlternate: boolean = false): Promise<void> {
    const oldProduct = this.discontinuedList[this.discontinuedList.length - 1];
    this.oldProduct = await this.conn.findCatalogWithAlternateProduct(oldProduct.productId);
    if (!checkAlternate && this.oldProduct?.get('alternateProduct')) {
      this.newProduct = this.oldProduct?.get('alternateProduct');
      this.isDiscontinued = true;
    } else if (checkAlternate && !this.oldProduct?.get('alternateProduct')) {
      const params1 = {
        productId: this.oldProduct.id,
        quantity: 1,
      };
      await this.conn.removeProductFromCart(params1);
      this.discontinuedList.pop();
      await this.getCart();
    }
  }

  async checkout(): Promise<void> {
    if (this.discontinuedList.length) {
      await this.checkdiscontinuedProduct();
    } else {
      const productList = [];
      this.cartItems.forEach((element: any): any => {
        if (this.aovExp && this.maxPrice.productID === element.productId) {
          if (element.quantity > 1) {
            const params1 = {
              productId: this.maxPrice.productID,
              quantity: 1,
            };
            this.removeProduct(params1);
          }
        }
        for (let index = 0; index < element.quantity; index += 1) {
          if (this.aovExp && productList.includes(this.maxPrice.productID)) {
            return;
          }
          productList.push(element.productId);
        }
      });
      const products = productList.toString();
      const url = 'user/checkout';
      const orderState = this.user?.get('orderState');
      const queryParams = {
        type: this.appConfig.Shared.Order?.Type.PRODUCT,
        products,
        useCureSkinCash: String(this.experimentService.shouldUseCureSkinCash(orderState)) // Convert to string to ensure it's always included
      };
      this.router.navigate([url], { queryParams });
      this.eventLogger.cleverTapEvent('click-checkout', JSON.stringify({ pageName: 'cart' }), [
        this.appConfig.Shared.EventTracker.MIXPANEL,
        this.appConfig.Shared.EventTracker.AMPLITUDE,
        this.appConfig.Shared.EventTracker.CLEVER_TAP,
        this.appConfig.Shared.EventTracker.MOENGAGE,
        this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
      ]);
    }
  }

  goToList(): void {
    const queryParams = { showAll: true };
    this.router.navigate(['user/order/reorder'], { queryParams });
  }

  async closePopup(isReplace: boolean = false): Promise<void> {
    this.popUp = { open: false };
    this.isDiscontinued = false;
    this.broadcast.broadcast('NAVIGATION_BACK');
    if (isReplace) {
      this.discontinuedList.pop();
      await this.getCart(true);
      this.getProductQuantity();
      this.fetchDiscount();
    }
  }

  async changeProduct(openArticle: boolean = false): Promise<void> {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'discontinued-product-changed' }));
    try {
      await this.conn.replaceDiscontinuedProduct(this.oldProduct.id, this.newProduct.id);
      this.replaceDiscontinuedProduct(openArticle);
    } catch (err) {
      const msg = JSON.parse(JSON.stringify(err));
      if (msg.message === 'A duplicate value for a field with unique values was provided') {
        this.replaceDiscontinuedProduct(openArticle);
      }
    }
  }

  async replaceDiscontinuedProduct(openArticle: boolean): Promise<any> {
    const params = {
      productId: this.newProduct.id,
      quantity: 1,
    };
    const quanity = this.productsInCart[this.oldProduct.id];
    for (let index = 0; index < quanity; index += 1) {
      // eslint-disable-next-line no-await-in-loop
      await this.conn.removeProductFromCart({ productId: this.oldProduct.id, quantity: 1 });
    }
    await this.conn.addProductToCart(params);
    if (!openArticle) {
      this.closePopup(true);
    }
  }

  async openArticle(): Promise<any> {
    await this.changeProduct(true);
    this.location.replaceState('cart');
    this.popUp = { open: false };
    this.isDiscontinued = false;
    this.viewProduct(this.newProduct);
  }

  viewProduct(product: any): void {
    const productData = JSON.parse(JSON.stringify(product));
    this.conn.navigateToURL(`/product/${productData.objectId}`);
  }

  goToShop(): any {
    this.router.navigate(['/user'], { queryParams: { tab: 'shop', back: 'home' } });
  }
  viewProductDetail(product: any): void {
    if (this.userService.isUnpaidUser(this.user)) return;
    this.conn.navigateToURL(`/product/${product.productId}`);
  }

  async addSingleProductToCart(productId: string): Promise<void> {
    const payload = {
      productId,
      quantity: 1,
      section: 'productsYouMayNeed',
    };
    await this.conn.addProductToNewCart(payload);
    await this.getCart();
    await this.getProductQuantity();
    await this.fetchDiscount();
  }

  scrollToBottom(): void {
    const scrollView = this.windowRef.nativeWindow.document.getElementById('newCartUIContainer');
    setTimeout((): void => {
      if (scrollView.scroll instanceof Function) {
        scrollView.scroll(0, scrollView.scrollHeight);
      }
    }, 0);
  }
}
